import { useFocusEffect, useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Image, Platform, StyleSheet, Text, View } from 'react-native';
import Svg, { Defs, LinearGradient, Rect, Stop } from 'react-native-svg';
import { useDispatch, useSelector } from 'react-redux';

import ChipPng from '../../../assets/chip.png';
import { IconRemoveFriendSvg } from '../../../assets/friends';
import ProfilePlaceholder from '../../../assets/header/player-icon.png';
import { IconCloseSvg } from '../../../assets/inventory';
import AddFriendIcon from '../../../assets/profileRedesign/add-friend.svg';
import CalendarIcon from '../../../assets/profileRedesign/calendar.svg';
import ChipsGainedIcon from '../../../assets/profileRedesign/chips-gained.png';
import EditIcon from '../../../assets/profileRedesign/edit.svg';
import LocationIcon from '../../../assets/profileRedesign/location.svg';
import LossesIcon from '../../../assets/profileRedesign/losses.png';
import OpenArrow from '../../../assets/profileRedesign/open-arrow.png';
import VipCrownPng from '../../../assets/profileRedesign/vip-crown.png';
import VipMembershipIcon from '../../../assets/profileRedesign/vip-membership.png';
import VipStatusCrown from '../../../assets/profileRedesign/vip-status-crown.svg';
import WinsIcon from '../../../assets/profileRedesign/wins.png';
import type { PlayerFriendshipStatus } from '../../../services/api';
import {
	donationTypeOfTab,
	getAnalyticsPlatform,
	trackDonationEvent,
} from '../../../services/donationAnalytics';
import {
	ChatStateWithPlayer,
	getChatStateWithPlayer,
	onDonationCompleted,
} from '../../../services/donationsApi';
import { goldGlow } from '../../../shared/glowStyles';
import { RootState } from '../../../store';
import {
	achievementsHubRequest,
	achievementsPlayerFeaturedRequest,
} from '../../../store/achievements/achievementsActions';
import {
	selectAchievementFamilies,
	selectAchievementsHubErrorKey,
	selectAchievementsHubLoading,
	selectAchievementsLoaded,
	selectFeaturedFamilyKeys,
	selectViewedFeatured,
} from '../../../store/achievements/achievementsReducer';
import { userStatsRequest } from '../../../store/auth/authActions';
import { selectIsLogged, selectProfile } from '../../../store/auth/authReducer';
import {
	friendRemove,
	friendRequestRespond,
	friendRequestSend,
} from '../../../store/friends/friendsActions';
import {
	selectFriendActionError,
	selectFriendBusy,
	selectFriendRelationOverride,
} from '../../../store/friends/friendsReducer';
import { getAvatarUrl } from '../../../utils/axiosInstance';
import { formatBigNumbers } from '../../../utils/helpers/numberFormat';
import { useSvgId } from '../../../utils/hooks/useSvgId';
import { RootStackParamList } from '../../navigator';
import {
	ACHIEVEMENTS_FONT,
	achievementsColors,
	GREEN_BUTTON_FILL,
	PANEL_WIDTH,
	RADIUS_MD,
} from '../Achievements/achievementsTheme';
import {
	achievementText,
	tierBadge,
	toAchievementViews,
} from '../Achievements/achievementsViewModel';
import ProfileTile, { ProfileTileItem } from '../Achievements/ProfileTile';
import { CtaButton } from '../common/CtaButton';
import { RedesignScrollView } from '../common/RedesignScrollView';
import { Pressable } from '../common/Touchable';
import { DuelProfileRow } from '../Duels/DuelProfileRow';
import { EVENT_FONT_CONDENSED } from '../Event/eventTheme';
import InventoryTabs, { InventoryTabKey } from '../Inventory/InventoryTabs';
import { ChatsIcon, ContactCardIcon, ProfileActionButton } from '../Profile/ProfileActionButton';
import ProfileChatActions from '../Profile/ProfileChatActions';
import ProfileRedesignEditView, {
	useEditProfileScale,
	useProfilePanel,
} from '../Profile/ProfileRedesignEditView';
import ProfileRedesignStatisticsView from '../Profile/ProfileRedesignStatisticsView';
import ProfileReportMenu from '../Profile/ProfileReportMenu';
import RedesignModal from './RedesignModal';

/**
 * Figma "Profile / Modal — Full content no vip" (4022:37706), page "BOGI DRAFT" - the profile in
 * the panel the achievements and inventory modals already use: the same "Tab / Modal" header, the
 * same surface/raised sections behind the duller orange hairline, and the same type ramp.
 *
 * The panel chrome lives in `RedesignModal`; everything here is the body the design draws, in its
 * 358-wide design space scaled once, the way those two do it.
 *
 * Inventory and Friends are that other modal's own tabs, so they hand over to it rather than being
 * rebuilt here - `InventoryScreen` hands the Profile tab back.
 */
type ProfilePlayer = {
	rank: number;
	playerId: string;
	username: string;
	avatarFileId: string | null;
	/** Absent where the source has no balance (the friends directory); the balance row hides. */
	totalChips?: number;
	level: number;
	isVip: boolean;
	isFriend?: boolean;
	/** The friends directory's relationship; wins over `isFriend` when both are given. */
	friendshipStatus?: PlayerFriendshipStatus;
	friendRequestId?: string | null;
};

type Props = {
	onClose: () => void;
	renderInline?: boolean;
	player?: ProfilePlayer;
	/**
	 * Set when this is rendered inline inside the inventory modal's own tab switching
	 * (`InventoryScreen`) rather than through the standalone `Profile` route: swaps the tab there
	 * by local state instead of navigating, so it stays the one modal screen the inventory and
	 * friends tabs already are, rather than replacing it with a second native-stack screen.
	 */
	onSelectTab?: (tab: InventoryTabKey) => void;
	initialEdit?: boolean;
	/**
	 * Closes the overlay route this profile was opened from, before it leaves for the donation
	 * store or the private chat. Needed where that route would otherwise stay on top of the
	 * destination: the notifications inbox (`Messages`), whose screen sits above the `BuyPackages`
	 * and `PrivateChat` routes this navigates to. Without it the profile closes alone.
	 */
	closeHost?: () => void;
};

type Nav = NativeStackNavigationProp<RootStackParamList>;

// "Header" (4022:37723): 68 tall. Figma's own frame insets the tab bar 12 and gives it 286, but
// this header now has to line up with the inventory modal's - the same tab row the profile tab
// switches to and from without unmounting the panel around it - so it matches that one's numbers
// (13 inset, a 292-wide row) instead: the two headers are built two different ways (this one a
// flex row, the inventory modal's own absolutely-positioned), and even a couple of pixels' gap
// between them reads as the tab bar jumping sideways on every switch. The same goes vertically:
// centred in the full 68 the tabs land at 17 and the close at 12, one above the inventory modal's
// 18 and 13, so `paddingTop` centres them in the 66 under it instead.
const HEADER = {
	height: 68,
	paddingTop: 2,
	paddingX: 13,
	gap: 8,
	tabsWidth: 292,
	close: 44,
	closeIcon: 24,
};
// The sections are 322 wide at x=12; the 24 on the right is the scroll indicator's lane.
const BODY = { paddingLeft: 12, paddingRight: 24, gap: 12, padding: 12 };
const SECTION = { radius: 12, padding: 12 };
const AVATAR = { ring: 111, crownWidth: 39.6, crownHeight: 34.65, badge: 41 };
const XP = { height: 28, radius: 14 };
const STAT = { artwork: 60, gap: 4, arrow: 22 };
const COINS = { icon: 32, iconHeight: 43 };
const VIP = { iconWidth: 36, iconHeight: 33, chevron: 18 };
/** "VIP / Active" (4022:38190): 96x32 on state/success/deep behind state/success/base. */
const ACTIVE = { width: 96, height: 32, gap: 5, crownWidth: 21, crownHeight: 16 };
const SUCCESS_DEEP = '#0D400D';
const SUCCESS_BASE = '#0CEB66';
const BUTTON = { width: 108, height: 28 };
const FEATURED_SLOTS = 5;
/**
 * A web deposit is credited when the provider's webhook lands, which can trail the checkout tab's
 * "success" by a moment, so the chat state is asked again once after this long.
 */
const DONATION_UNLOCK_RECHECK_MS = 3000;
/** VIP can be donated from any browser, mobile web included, but never from the apps. */
const CAN_DONATE_VIP = Platform.OS === 'web';

/** "Arcade / Play button" (4022:38003 / 4022:38011): the 108x28 green pill on the two rows. */
const GreenPill = ({
	label,
	onPress,
	styles,
}: {
	label: string;
	onPress?: () => void;
	styles: ReturnType<typeof createStyles>;
}) => {
	const fillId = useSvgId('profileGreenPill');

	return (
		<Pressable
			accessibilityRole='button'
			accessibilityLabel={label}
			disabled={!onPress}
			onPress={onPress}
			style={styles.greenPill}
		>
			<View pointerEvents='none' style={StyleSheet.absoluteFill}>
				<Svg width='100%' height='100%'>
					<Defs>
						<LinearGradient id={fillId} x1='0' y1='0' x2='0' y2='1'>
							{GREEN_BUTTON_FILL.map(([offset, color]) => (
								<Stop key={offset} offset={offset} stopColor={color} />
							))}
						</LinearGradient>
					</Defs>
					<Rect x='0' y='0' width='100%' height='100%' fill={`url(#${fillId})`} />
				</Svg>
			</View>
			<Text numberOfLines={1} style={styles.greenPillLabel}>
				{label}
			</Text>
		</Pressable>
	);
};

const ProfileModal = ({
	onClose,
	renderInline = false,
	player,
	onSelectTab,
	initialEdit = false,
	closeHost,
}: Props) => {
	const dispatch = useDispatch();
	const { t } = useTranslation();
	const navigation = useNavigation<Nav>();
	const user = useSelector(selectProfile);
	const isLogged = useSelector(selectIsLogged);
	const achievementFamilies = useSelector(selectAchievementFamilies);
	const featuredFamilyKeys = useSelector(selectFeaturedFamilyKeys);
	const achievementsLoaded = useSelector(selectAchievementsLoaded);
	const achievementsHubLoading = useSelector(selectAchievementsHubLoading);
	const achievementsHubErrorKey = useSelector(selectAchievementsHubErrorKey);
	const viewedFeatured = useSelector(selectViewedFeatured);
	const stats = useSelector((state: RootState) => state.auth.statistics);
	const statsLoading = useSelector((state: RootState) => state.auth.statisticsLoading === true);
	const viewedId = player ? String(player.playerId) : '';
	const relationOverride = useSelector((state: RootState) =>
		selectFriendRelationOverride(state, viewedId),
	);
	const friendBusy = useSelector((state: RootState) => selectFriendBusy(state, viewedId));
	const friendActionError = useSelector(selectFriendActionError);
	const friendshipStatus: PlayerFriendshipStatus =
		relationOverride?.status ?? player?.friendshipStatus ?? (player?.isFriend ? 'FRIENDS' : 'NONE');
	const friendRequestId = relationOverride
		? relationOverride.friendRequestId
		: (player?.friendRequestId ?? null);
	const [isEditing, setIsEditing] = useState(initialEdit);
	const [isViewingStatistics, setIsViewingStatistics] = useState(false);
	const xpFillId = useSvgId('profileXpFill');

	// The profile and its edit view are one panel: the same scale and the same size, so opening
	// or leaving the edit view never resizes it.
	const scale = useEditProfileScale();
	const panel = useProfilePanel();
	const styles = useMemo(() => createStyles(scale), [scale]);

	const profile = player
		? {
				id: player.playerId,
				username: player.username,
				avatarFileId: player.avatarFileId,
				chips: player.totalChips,
				level: player.level,
				isVip: player.isVip,
			}
		: user;
	const vipMembershipRoute = profile?.isVip ? 'VipActiveMember' : 'VipSubscription';

	useEffect(() => {
		dispatch(userStatsRequest(player?.playerId));
	}, [dispatch, player?.playerId]);

	useEffect(() => {
		if (initialEdit) setIsEditing(true);
	}, [initialEdit]);

	const level = profile?.level ?? 1;
	const isOwnProfile = !player || String(player.playerId) === String(user?.id);
	/**
	 * Another player's balance comes with the opener (The Richest) or else with their statistics.
	 * Those land in the same store slot as anyone's, so they are not read while a request is out:
	 * the previous player's balance would show until it lands.
	 */
	const chips =
		isOwnProfile || profile?.chips != null
			? profile?.chips
			: statsLoading
				? undefined
				: (stats?.wallet?.chips ?? undefined);
	const location = player ? stats?.player.location : (user?.location ?? stats?.player.location);
	const age = player ? stats?.player.age : (user?.age ?? stats?.player.age);
	const description = (
		player ? stats?.player.description : (user?.description ?? stats?.player.description)
	)?.trim();
	const games = stats?.gamesData;
	// Despite the names, the backend sends the XP earned inside the current level as
	// `svarka_minimal_experience` and the XP that level takes as `svarka_maximal_experience`.
	const levelExperience = Math.max(0, games?.svarka_minimal_experience ?? 0);
	const levelExperienceRequired = Math.max(0, games?.svarka_maximal_experience ?? 0);
	const levelProgress =
		levelExperienceRequired > 0 ? Math.min(levelExperience / levelExperienceRequired, 1) : 0;
	const statItems = [
		{
			label: t('T_SITE_PROFILE_WINS', { defaultValue: 'Wins' }).toUpperCase(),
			value: games?.svarka_wins ?? 0,
			icon: WinsIcon,
		},
		{
			label: t('T_SITE_PROFILE_LOSSES', { defaultValue: 'Losses' }).toUpperCase(),
			value: games?.svarka_loses ?? 0,
			icon: LossesIcon,
		},
		{
			label: t('T_SITE_PROFILE_CHIUPS_GAINED', { defaultValue: 'Chips gained' }).toUpperCase(),
			value: games?.svarka_chips_wins ?? 0,
			icon: ChipsGainedIcon,
		},
	];

	const viewedPlayerId = isOwnProfile || !player ? null : String(player.playerId);

	useEffect(() => {
		if (
			!isLogged ||
			!isOwnProfile ||
			achievementsLoaded ||
			achievementsHubLoading ||
			achievementsHubErrorKey
		) {
			return;
		}

		dispatch(achievementsHubRequest());
	}, [
		isLogged,
		isOwnProfile,
		achievementsLoaded,
		achievementsHubLoading,
		achievementsHubErrorKey,
		dispatch,
	]);

	useEffect(() => {
		if (!isLogged || !viewedPlayerId) return;

		dispatch(achievementsPlayerFeaturedRequest(viewedPlayerId));
	}, [isLogged, viewedPlayerId, dispatch]);

	// Another player's private chat: locked until a donation between the two is delivered. Asked
	// on open, whenever the screen under the profile regains focus (back from the donation store
	// or the chat), and when a donation to this player completes - so the unlocked state shows
	// straight away.
	const chatPlayerId = isLogged ? viewedPlayerId : null;
	const chatPlayerIdRef = useRef(chatPlayerId);
	chatPlayerIdRef.current = chatPlayerId;
	const [chatState, setChatState] = useState<ChatStateWithPlayer | null>(null);

	useEffect(() => {
		setChatState(null);
	}, [chatPlayerId]);

	const refreshChatState = useCallback(() => {
		if (!chatPlayerId) return;

		getChatStateWithPlayer(chatPlayerId)
			.then(state => {
				if (chatPlayerIdRef.current === chatPlayerId) setChatState(state);
			})
			.catch(error => {
				console.warn('[Profile] Failed to load the private chat state:', error?.message);
			});
	}, [chatPlayerId]);

	useFocusEffect(refreshChatState);

	useEffect(() => {
		if (!chatPlayerId) return;

		let recheck: ReturnType<typeof setTimeout> | null = null;
		const unsubscribe = onDonationCompleted(recipientPlayerId => {
			if (recipientPlayerId !== chatPlayerId) return;

			refreshChatState();
			if (recheck) clearTimeout(recheck);
			recheck = setTimeout(refreshChatState, DONATION_UNLOCK_RECHECK_MS);
		});

		return () => {
			unsubscribe();
			if (recheck) clearTimeout(recheck);
		};
	}, [chatPlayerId, refreshChatState]);

	const openDonationStore = (activeTab: 'chips' | 'vip') => {
		if (!player) return;

		const recipientPlayerId = String(player.playerId);
		trackDonationEvent('donation_profile_action_clicked', {
			sender_id: user?.id != null ? String(user.id) : '',
			recipient_id: recipientPlayerId,
			donation_type: donationTypeOfTab(activeTab),
			platform: getAnalyticsPlatform(),
		});
		// The profile is portalled over every screen, so it closes first or the store opens under it.
		onClose();
		closeHost?.();
		navigation.navigate('BuyPackages', {
			recipient: { playerId: recipientPlayerId, username: player.username },
			activeTab,
		});
	};

	const openPrivateChat = () => {
		if (!player) return;

		const playerId = String(player.playerId);
		onClose();
		closeHost?.();
		navigation.navigate('PrivateChat', { playerId, username: player.username });
	};

	// Both leave the profile for a modal of their own, so it closes first, as the chat does.
	const openBlockList = () => {
		onClose();
		closeHost?.();
		navigation.navigate('BlockList');
	};

	const openMyChats = () => {
		onClose();
		closeHost?.();
		navigation.navigate('Messages', { tab: 'chats' });
	};

	const featuredTiles = useMemo((): Array<{ key: string; item: ProfileTileItem }> => {
		if (isOwnProfile) {
			const views = toAchievementViews(achievementFamilies, featuredFamilyKeys);

			return featuredFamilyKeys.flatMap(familyKey => {
				const view = views.find(candidate => candidate.familyKey === familyKey);
				return view && !view.locked
					? [
							{
								key: familyKey,
								item: { label: t(view.shownTier.nameKey), renderArtwork: view.renderBadge },
							},
						]
					: [];
			});
		}

		if (!viewedPlayerId || viewedFeatured.playerId !== viewedPlayerId) return [];

		return viewedFeatured.featured.map(entry => ({
			key: entry.familyKey,
			item: {
				label: t(entry.nameKey),
				renderArtwork: tierBadge({
					displayTier: entry.displayTier,
					iconFileId: entry.iconFileId,
					unlocked: true,
					iconKey: entry.iconKey,
				}),
			},
		}));
	}, [isOwnProfile, achievementFamilies, featuredFamilyKeys, viewedPlayerId, viewedFeatured, t]);
	const featured = Array.from({ length: FEATURED_SLOTS }, (_slot, index) => featuredTiles[index]);

	// Inventory and Friends are the inventory modal's tabs; Profile is this one. When rendered
	// inline inside that modal, `onSelectTab` just flips its local tab state; the `navigate`
	// fallback is only for the standalone `Profile` route, which redirects there on mount anyway
	// (see `ProfileScreen`) so it should not normally run.
	const selectTab = (tab: InventoryTabKey) => {
		if (tab === 'profile') return;

		if (onSelectTab) {
			onSelectTab(tab);
			return;
		}

		navigation.navigate('Inventory', { tab });
	};

	const openAchievements = () => navigation.navigate('Achievements');

	if (isViewingStatistics) {
		return (
			<ProfileRedesignStatisticsView
				onBack={() => setIsViewingStatistics(false)}
				onClose={onClose}
				renderInline={renderInline}
				playerId={isOwnProfile ? undefined : player?.playerId}
			/>
		);
	}

	if (isEditing && isOwnProfile) {
		return (
			<ProfileRedesignEditView
				onBack={() => setIsEditing(false)}
				onClose={onClose}
				onBuyUsernameChanger={() =>
					navigation.navigate('Inventory', { tab: 'inventory', category: 'featured' })
				}
				renderInline={renderInline}
			/>
		);
	}

	return (
		<RedesignModal
			scale={scale}
			onClose={onClose}
			renderInline={renderInline}
			panel={panel}
			desktopTwin
		>
			{/* Another player's profile has no tabs, so its header is only the close button: it floats
				over the panel's corner instead of taking a row of its own. */}
			<View
				style={[styles.header, !isOwnProfile && styles.headerFloating]}
				pointerEvents={isOwnProfile ? 'auto' : 'box-none'}
			>
				{isOwnProfile ? (
					<InventoryTabs
						value='profile'
						onChange={selectTab}
						rowWidth={HEADER.tabsWidth}
						scale={scale}
					/>
				) : (
					<View style={styles.headerSpacer} />
				)}

				<Pressable
					accessibilityRole='button'
					accessibilityLabel={t('T_SITE_A11Y_CLOSE_PROFILE', { defaultValue: 'Close profile' })}
					onPress={onClose}
					style={styles.close}
				>
					{/* Wrapped for the same painting-order reason as the tab icons. */}
					<View>
						<IconCloseSvg width={HEADER.closeIcon * scale} height={HEADER.closeIcon * scale} />
					</View>
				</Pressable>
			</View>

			{/* The track sits 12 in from the body's right edge, in the 24 the content leaves. */}
			<RedesignScrollView
				contentContainerStyle={[styles.content, !isOwnProfile && styles.contentUnderFloatingClose]}
				indicatorInsets={{ right: 12 * scale }}
			>
				{/* "Player details" (4022:37737) */}
				<View style={styles.section}>
					<View style={styles.identityRow}>
						<Pressable
							accessibilityRole='button'
							accessibilityLabel={t('T_SITE_A11Y_EDIT_PROFILE_PHOTO', {
								defaultValue: 'Edit profile photo',
							})}
							disabled={!isOwnProfile}
							onPress={() => setIsEditing(true)}
							style={styles.avatarWrap}
						>
							{profile?.isVip === true ? (
								<Image source={VipCrownPng} resizeMode='contain' style={styles.crown} />
							) : null}
							<View style={styles.avatarGlow}>
								<View style={styles.avatarRing}>
									{profile?.avatarFileId ? (
										<Image
											source={{ uri: getAvatarUrl(profile.avatarFileId) }}
											style={styles.avatar}
										/>
									) : (
										<Image source={ProfilePlaceholder} resizeMode='cover' style={styles.avatar} />
									)}
								</View>
							</View>
							{/* "Level Badge" (4022:37919): a 41 gold disc with the level in black. */}
							<View style={styles.levelBadge}>
								<Text style={styles.levelText}>{level}</Text>
							</View>
						</Pressable>

						<View style={styles.identity}>
							<View style={styles.usernameRow}>
								<Text numberOfLines={1} style={styles.identityText}>
									{profile?.username ??
										t('T_SITE_PROFILE_DEFAULT_NAME', { defaultValue: 'Player' })}
								</Text>
								{isOwnProfile ? (
									<Pressable
										accessibilityRole='button'
										accessibilityLabel={t('T_SITE_PROFILE_EDIT', { defaultValue: 'Edit profile' })}
										onPress={() => setIsEditing(true)}
										style={styles.editButton}
									>
										<EditIcon width={12 * scale} height={12 * scale} />
									</Pressable>
								) : null}
							</View>
							<View style={styles.identityDetail}>
								<LocationIcon width={22 * scale} height={22 * scale} />
								<Text numberOfLines={1} style={styles.identityText}>
									{location || '-'}
								</Text>
							</View>
							<View style={styles.identityDetail}>
								<CalendarIcon width={22 * scale} height={22 * scale} />
								<Text numberOfLines={1} style={styles.identityText}>
									{age
										? t('T_SITE_PROFILE_AGE_YEARS', { defaultValue: '{{age}} years old', age })
										: '-'}
								</Text>
							</View>
						</View>
					</View>

					<Text style={styles.bio}>
						{description ||
							t('T_SITE_PROFILE_DEFAULT_BIO', {
								defaultValue: 'Just a player who loves the game!',
							})}
					</Text>
				</View>

				{/* "Level progress" (4022:37965) */}
				<View style={styles.section}>
					<View style={styles.levelLabels}>
						<Text style={styles.levelLabel}>
							{t('T_SITE_PROFILE_LEVEL', { defaultValue: 'Level' })} {level}
						</Text>
						<Text style={styles.levelLabel}>
							{t('T_SITE_PROFILE_LEVEL', { defaultValue: 'Level' })} {level + 1}
						</Text>
					</View>
					<View style={styles.xpTrack}>
						<View style={[styles.xpFill, { width: `${levelProgress * 100}%` }]}>
							<Svg width='100%' height='100%'>
								<Defs>
									<LinearGradient id={xpFillId} x1='0' y1='0' x2='0' y2='1'>
										{GREEN_BUTTON_FILL.map(([offset, color]) => (
											<Stop key={offset} offset={offset} stopColor={color} />
										))}
									</LinearGradient>
								</Defs>
								<Rect x='0' y='0' width='100%' height='100%' fill={`url(#${xpFillId})`} />
							</Svg>
						</View>
						<Text style={styles.xpText}>
							{formatBigNumbers(levelExperience)} / {formatBigNumbers(levelExperienceRequired)}{' '}
							{t('T_SITE_PROFILE_XP', { defaultValue: 'XP' })}
						</Text>
					</View>
				</View>

				{/* "Player statistics" (4022:37972) */}
				<View style={[styles.section, styles.statistics]}>
					{statItems.map(item => (
						<View key={item.label} style={styles.stat}>
							<Image source={item.icon} resizeMode='contain' style={styles.statImage} />
							<Text numberOfLines={1} style={styles.statValue}>
								{formatBigNumbers(Number(item.value) || 0)}
							</Text>
							<Text numberOfLines={1} style={styles.statLabel}>
								{item.label}
							</Text>
						</View>
					))}
					<Pressable
						accessibilityRole='button'
						accessibilityLabel={t('T_SITE_A11Y_OPEN_STATISTICS', {
							defaultValue: 'Open statistics',
						})}
						hitSlop={12 * scale}
						onPress={() => setIsViewingStatistics(true)}
					>
						<Image source={OpenArrow} resizeMode='contain' style={styles.chevron} />
					</Pressable>
				</View>

				{/* "Coins and donation" (4022:37989): BUY CHIPS on your own profile, DONATE CHIPS on
				    another player's, whose balance shows a dash until their statistics arrive. */}
				{(isOwnProfile ? chips != null : !!player) ? (
					<View style={[styles.section, styles.row]}>
						<Image source={ChipPng} style={styles.chip} resizeMode='contain' />
						<View style={styles.balanceCopy}>
							<Text style={styles.balanceLabel}>
								{t('T_SITE_PROFILE_COINS_BALANCE', { defaultValue: 'Coins Balance' })}
							</Text>
							<Text numberOfLines={1} style={styles.balanceValue}>
								{chips != null ? formatBigNumbers(chips) : '-'}
							</Text>
						</View>
						{isOwnProfile ? (
							<GreenPill
								label={t('T_SITE_PROFILE_BUY', { defaultValue: 'Buy chips' }).toUpperCase()}
								onPress={() => navigation.navigate('BuyPackages', { activeTab: 'chips' })}
								styles={styles}
							/>
						) : (
							<GreenPill
								label={t('T_SITE_PROFILE_DONATE_CHIPS', { defaultValue: 'DONATE CHIPS' })}
								onPress={() => openDonationStore('chips')}
								styles={styles}
							/>
						)}
					</View>
				) : null}

				<DuelProfileRow
					playerId={isOwnProfile ? user?.id : player?.playerId}
					scale={scale}
					style={styles.section}
					onOpen={isOwnProfile ? () => navigation.navigate('Duels') : undefined}
					arrow={<Image source={OpenArrow} resizeMode='contain' style={styles.vipChevron} />}
				/>

				{/* "VIP Membership" (4022:38009): Active, or BUY VIP on your own profile and DONATE VIP
				    on another player's - web only, so the apps show the row only for an active VIP. */}
				{isOwnProfile || profile?.isVip === true || CAN_DONATE_VIP ? (
					<View style={[styles.section, styles.row]}>
						<Image source={VipMembershipIcon} resizeMode='contain' style={styles.vipIcon} />
						<Text numberOfLines={1} style={styles.vipLabel}>
							{t('T_SITE_VIP_MEMBERSHIP', { defaultValue: 'VIP Membership' })}
						</Text>
						{/* The file pins the pill and the chevron over the label's own width rather than
						    letting them squeeze it (4022:38011 / 4022:38195). Another player's row has no
						    chevron, so its action sits in the balance row's button column, centred. */}
						{profile?.isVip === true ? (
							<View style={isOwnProfile ? styles.vipAction : styles.vipActionColumn}>
								<View style={styles.activeBadge}>
									{/* The crown is 21x16, not square - drawing it in a square box cropped it. */}
									<View>
										<VipStatusCrown
											width={ACTIVE.crownWidth * scale}
											height={ACTIVE.crownHeight * scale}
										/>
									</View>
									<Text numberOfLines={1} style={styles.activeText}>
										{t('T_SITE_REFERRAL_ACTIVE', { defaultValue: 'Active' })}
									</Text>
								</View>
							</View>
						) : isOwnProfile ? (
							<View style={styles.vipAction}>
								<GreenPill
									label={t('T_SITE_BUY_VIP', { defaultValue: 'BUY VIP' }).toUpperCase()}
									onPress={() => navigation.navigate('VipSubscription')}
									styles={styles}
								/>
							</View>
						) : (
							<View style={styles.vipActionColumn}>
								<GreenPill
									label={t('T_SITE_DONATION_DONATE_VIP', { defaultValue: 'DONATE VIP' })}
									onPress={() => openDonationStore('vip')}
									styles={styles}
								/>
							</View>
						)}
						{isOwnProfile ? (
							<Pressable
								accessibilityRole='button'
								accessibilityLabel={t('T_SITE_A11Y_OPEN_VIP', { defaultValue: 'Open VIP' })}
								onPress={() => navigation.navigate(vipMembershipRoute)}
								style={styles.vipOpen}
							>
								<Image source={OpenArrow} resizeMode='contain' style={styles.vipChevron} />
							</Pressable>
						) : null}
					</View>
				) : null}

				{/* "Featured Achievements" (4022:38205) - the achievements modal's own profile tiles. */}
				<View style={styles.section}>
					<View style={styles.achievementsHeading}>
						<Text numberOfLines={1} style={styles.sectionHeading}>
							{achievementText(t, 'T_SITE_ACHIEVEMENTS_FEATURED')}
						</Text>
						{isOwnProfile ? (
							<Pressable
								accessibilityRole='button'
								onPress={openAchievements}
								style={styles.viewAll}
							>
								<Text style={styles.viewAllLabel}>
									{achievementText(t, 'T_SITE_ACHIEVEMENTS_VIEW_ALL')}
								</Text>
								<Image source={OpenArrow} resizeMode='contain' style={styles.chevron} />
							</Pressable>
						) : null}
					</View>
					<View style={styles.achievements}>
						{featured.map((tile, index) => (
							<ProfileTile
								key={tile?.key ?? `empty-${index}`}
								item={tile?.item}
								onPress={isOwnProfile ? openAchievements : undefined}
								removable={false}
								scale={scale}
							/>
						))}
					</View>
				</View>

				{isOwnProfile ? (
					<View style={styles.ownActions}>
						<ProfileActionButton
							label={t('T_SITE_BLOCK_LIST', { defaultValue: 'Block list' })}
							icon={ContactCardIcon}
							variant='red'
							scale={scale}
							onPress={openBlockList}
						/>
						<ProfileActionButton
							label={t('T_SITE_CHAT_MY_PRIVATE_CHATS', { defaultValue: 'My private chats' })}
							icon={ChatsIcon}
							variant='gold'
							scale={scale}
							onPress={openMyChats}
						/>
					</View>
				) : null}

				{/* Another player's private chat, once a donation between the two has unlocked it. */}
				{!isOwnProfile && player && chatState?.unlocked ? (
					<ProfileChatActions
						playerId={String(player.playerId)}
						username={player.username}
						scale={scale}
						onOpenChat={openPrivateChat}
					/>
				) : null}

				{!isOwnProfile && player ? (
					<View style={styles.profileActions}>
						{/* Half the row, as the BLOCK CHAT / PRIVATE CHAT pair above. Above the add-friend
							button, so the menu it opens is not painted under it. */}
						<View style={styles.reportSlot}>
							<ProfileReportMenu
								playerId={String(player.playerId)}
								username={player.username}
								scale={scale}
								// BLOCK CHAT above does the blocking once the chat is unlocked.
								canBlock={!chatState?.unlocked}
							/>
						</View>
						{friendshipStatus === 'NONE' ? (
							<CtaButton
								accessibilityLabel={t('T_SITE_A11Y_ADD_FRIEND', { defaultValue: 'Add friend' })}
								icon={<AddFriendIcon width={20 * scale} height={23 * scale} />}
								label={
									friendBusy
										? t('T_SITE_FRIENDS_SENDING', { defaultValue: 'Sending...' })
										: t('T_SITE_FRIENDS_ADD', { defaultValue: 'Add Friend' })
								}
								onPress={
									friendBusy ? undefined : () => dispatch(friendRequestSend({ playerId: viewedId }))
								}
								style={styles.addFriendButton}
								labelStyle={styles.addFriendLabel}
								surfaceStyle={styles.addFriendSurface}
							/>
						) : null}
						{friendshipStatus === 'FRIENDS' ? (
							<CtaButton
								accessibilityLabel={t('T_SITE_A11Y_UNFRIEND', {
									defaultValue: 'Remove {{username}} from friends',
									username: player.username,
								})}
								icon={<IconRemoveFriendSvg width={20 * scale} height={20 * scale} />}
								label={
									friendBusy
										? t('T_SITE_FRIENDS_REMOVING', { defaultValue: 'Removing...' })
										: t('T_SITE_FRIENDS_UNFRIEND', { defaultValue: 'Unfriend' })
								}
								variant='danger'
								onPress={
									friendBusy ? undefined : () => dispatch(friendRemove({ playerId: viewedId }))
								}
								style={styles.addFriendButton}
								labelStyle={styles.addFriendLabel}
								surfaceStyle={styles.addFriendSurface}
							/>
						) : null}
						{friendshipStatus === 'PENDING_OUTGOING' ? (
							<CtaButton
								accessibilityLabel={t('T_SITE_A11Y_FRIEND_REQUEST_SENT', {
									defaultValue: 'Friend request sent',
								})}
								label={t('T_SITE_FRIENDS_REQUEST_SENT_SHORT', { defaultValue: 'Request Sent' })}
								style={[styles.addFriendButton, styles.friendButtonSettled]}
								labelStyle={styles.addFriendLabel}
								surfaceStyle={styles.addFriendSurface}
							/>
						) : null}
						{friendshipStatus === 'PENDING_INCOMING' && friendRequestId ? (
							<CtaButton
								accessibilityLabel={t('T_SITE_A11Y_ACCEPT_FRIEND_REQUEST', {
									defaultValue: 'Accept friend request',
								})}
								icon={<AddFriendIcon width={20 * scale} height={23 * scale} />}
								label={
									friendBusy
										? t('T_SITE_FRIENDS_ACCEPTING', { defaultValue: 'Accepting...' })
										: t('T_SITE_FRIENDS_ACCEPT', { defaultValue: 'Accept Friend' })
								}
								onPress={
									friendBusy
										? undefined
										: () =>
												dispatch(
													friendRequestRespond({
														playerId: viewedId,
														requestId: friendRequestId,
														accept: true,
													}),
												)
								}
								style={styles.addFriendButton}
								labelStyle={styles.addFriendLabel}
								surfaceStyle={styles.addFriendSurface}
							/>
						) : null}
					</View>
				) : null}
				{!isOwnProfile && player && friendActionError?.playerId === viewedId ? (
					<Text style={styles.friendError}>{friendActionError.message}</Text>
				) : null}
			</RedesignScrollView>
		</RedesignModal>
	);
};

const createStyles = (scale: number) =>
	StyleSheet.create({
		header: {
			width: PANEL_WIDTH * scale,
			height: HEADER.height * scale,
			flexDirection: 'row',
			alignItems: 'center',
			gap: HEADER.gap * scale,
			paddingTop: HEADER.paddingTop * scale,
			paddingLeft: HEADER.paddingX * scale,
		},
		headerFloating: {
			position: 'absolute',
			// Inside the profile card's top-right corner: the 24 the body leaves on the right puts the
			// icon about 10 in from the card's edge, and 8 down about 10 below its top.
			top: 8 * scale,
			right: 0,
			zIndex: 3,
			height: (HEADER.close + 8) * scale,
			paddingTop: 0,
			paddingRight: 24 * scale,
		},
		headerSpacer: {
			flex: 1,
		},
		close: {
			width: HEADER.close * scale,
			height: HEADER.close * scale,
			alignItems: 'center',
			justifyContent: 'center',
		},
		content: {
			paddingLeft: BODY.paddingLeft * scale,
			paddingRight: BODY.paddingRight * scale,
			paddingBottom: BODY.padding * scale,
			rowGap: BODY.gap * scale,
		},
		contentUnderFloatingClose: {
			paddingTop: BODY.padding * scale,
		},
		// Every section is the file's surface/raised plate behind the duller orange hairline.
		section: {
			padding: SECTION.padding * scale,
			borderRadius: SECTION.radius * scale,
			borderWidth: Math.max(1, 1 * scale),
			borderColor: achievementsColors.panelOrange,
			backgroundColor: achievementsColors.surface,
		},
		row: {
			flexDirection: 'row',
			alignItems: 'center',
			gap: BODY.gap * scale,
		},
		identityRow: {
			flexDirection: 'row',
			alignItems: 'center',
		},
		avatarWrap: {
			width: (AVATAR.ring + 22) * scale,
			height: 133 * scale,
			alignItems: 'center',
			justifyContent: 'flex-end',
		},
		crown: {
			position: 'absolute',
			top: 0,
			width: AVATAR.crownWidth * scale,
			height: AVATAR.crownHeight * scale,
			zIndex: 2,
		},
		avatarGlow: {
			width: AVATAR.ring * scale,
			height: AVATAR.ring * scale,
			borderRadius: (AVATAR.ring / 2) * scale,
			...goldGlow,
		},
		avatarRing: {
			width: '100%',
			height: '100%',
			borderRadius: (AVATAR.ring / 2) * scale,
			borderWidth: 3 * scale,
			borderColor: '#FFE46D',
			backgroundColor: '#190301',
			overflow: 'hidden',
		},
		avatar: { width: '100%', height: '100%' },
		levelBadge: {
			position: 'absolute',
			left: 0,
			bottom: 2 * scale,
			width: AVATAR.badge * scale,
			height: AVATAR.badge * scale,
			borderRadius: (AVATAR.badge / 2) * scale,
			borderWidth: 2 * scale,
			borderColor: '#FFE46D',
			backgroundColor: '#F6C90E',
			alignItems: 'center',
			justifyContent: 'center',
		},
		// Sofia Sans Black 24 in text/on-badge black.
		levelText: {
			color: '#000000',
			fontFamily: ACHIEVEMENTS_FONT,
			fontSize: 24 * scale,
			fontWeight: '900',
			lineHeight: 24 * 1.2 * scale,
		},
		identity: {
			flex: 1,
			rowGap: BODY.gap * scale,
		},
		usernameRow: {
			flexDirection: 'row',
			alignItems: 'center',
			columnGap: 10 * scale,
		},
		identityDetail: {
			flexDirection: 'row',
			alignItems: 'center',
			columnGap: 8 * scale,
		},
		// Figma "DS/Body/M": Sofia Sans Medium 14, line-height 1.4, text/accent.
		identityText: {
			flexShrink: 1,
			color: achievementsColors.accent,
			fontFamily: ACHIEVEMENTS_FONT,
			fontSize: 14 * scale,
			fontWeight: '500',
			lineHeight: 14 * 1.4 * scale,
		},
		editButton: {
			width: 22 * scale,
			height: 22 * scale,
			alignItems: 'center',
			justifyContent: 'center',
			borderRadius: RADIUS_MD * scale,
			borderWidth: Math.max(1, 1 * scale),
			borderColor: achievementsColors.panelOrange,
			backgroundColor: '#5A1005',
		},
		bio: {
			marginTop: 16 * scale,
			color: achievementsColors.accent,
			fontFamily: ACHIEVEMENTS_FONT,
			fontSize: 14 * scale,
			fontWeight: '500',
			lineHeight: 14 * 1.4 * scale,
			textAlign: 'center',
		},
		levelLabels: {
			flexDirection: 'row',
			justifyContent: 'space-between',
			marginBottom: 16 * scale,
		},
		// "Numeric/Balance": Sofia Sans Bold 16, line-height 16, letter-spacing -0.2813.
		levelLabel: {
			color: achievementsColors.accent,
			fontFamily: ACHIEVEMENTS_FONT,
			fontSize: 16 * scale,
			fontWeight: '700',
			lineHeight: 16 * scale,
			letterSpacing: -0.2813 * scale,
		},
		xpTrack: {
			height: XP.height * scale,
			borderRadius: XP.radius * scale,
			backgroundColor: achievementsColors.overlay,
			overflow: 'hidden',
			alignItems: 'center',
			justifyContent: 'center',
		},
		xpFill: {
			position: 'absolute',
			left: 0,
			top: 0,
			bottom: 0,
			borderRadius: XP.radius * scale,
			overflow: 'hidden',
		},
		xpText: {
			color: achievementsColors.gold50,
			fontFamily: ACHIEVEMENTS_FONT,
			fontSize: 14 * scale,
			fontWeight: '500',
			lineHeight: 14 * 1.4 * scale,
		},
		statistics: {
			flexDirection: 'row',
			alignItems: 'center',
			gap: STAT.gap * scale,
		},
		stat: {
			flex: 1,
			alignItems: 'center',
			rowGap: STAT.gap * scale,
		},
		statImage: {
			width: STAT.artwork * scale,
			height: STAT.artwork * scale,
		},
		statValue: {
			color: achievementsColors.gold200,
			fontFamily: ACHIEVEMENTS_FONT,
			fontSize: 16 * scale,
			fontWeight: '700',
			lineHeight: 16 * scale,
			letterSpacing: -0.2813 * scale,
			textAlign: 'center',
		},
		// "DS/Body/S": Sofia Sans Medium 12, line-height 1.4, gold/50.
		statLabel: {
			color: achievementsColors.gold50,
			fontFamily: ACHIEVEMENTS_FONT,
			fontSize: 12 * scale,
			fontWeight: '500',
			lineHeight: 12 * 1.4 * scale,
			textAlign: 'center',
		},
		chevron: {
			width: STAT.arrow * scale,
			height: STAT.arrow * scale,
		},
		chip: {
			width: COINS.icon * scale,
			height: COINS.iconHeight * scale,
		},
		balanceCopy: {
			flex: 1,
		},
		balanceLabel: {
			color: achievementsColors.gold50,
			fontFamily: ACHIEVEMENTS_FONT,
			fontSize: 14 * scale,
			fontWeight: '500',
			lineHeight: 14 * 1.4 * scale,
		},
		// "DS/Numeric/L": Sofia Sans Bold 24, line-height 1, letter-spacing -0.24, gold/200.
		balanceValue: {
			color: achievementsColors.gold200,
			fontFamily: ACHIEVEMENTS_FONT,
			fontSize: 24 * scale,
			fontWeight: '700',
			lineHeight: 24 * scale,
			letterSpacing: -0.24 * scale,
		},
		vipIcon: {
			width: VIP.iconWidth * scale,
			height: VIP.iconHeight * scale,
		},
		vipLabel: {
			width: 228 * scale,
			color: achievementsColors.accent,
			fontFamily: ACHIEVEMENTS_FONT,
			fontSize: 16 * scale,
			fontWeight: '700',
			lineHeight: 16 * scale,
			letterSpacing: -0.2813 * scale,
		},
		vipAction: {
			position: 'absolute',
			left: 175 * scale,
			top: 14 * scale,
		},
		// The balance row's pill column: flush with the content edge and as wide as the pill.
		vipActionColumn: {
			position: 'absolute',
			right: SECTION.padding * scale,
			top: 14 * scale,
			width: BUTTON.width * scale,
			alignItems: 'center',
		},
		vipOpen: {
			position: 'absolute',
			right: 12 * scale,
			top: 19.5 * scale,
		},
		vipChevron: {
			width: VIP.chevron * scale,
			height: VIP.chevron * scale,
		},
		activeBadge: {
			width: ACTIVE.width * scale,
			height: ACTIVE.height * scale,
			flexDirection: 'row',
			alignItems: 'center',
			justifyContent: 'center',
			columnGap: ACTIVE.gap * scale,
			borderRadius: 16 * scale,
			borderWidth: Math.max(1, 1 * scale),
			borderColor: SUCCESS_BASE,
			backgroundColor: SUCCESS_DEEP,
			overflow: 'hidden',
		},
		// Sofia Sans SemiBold 12, line-height 1.4, letter-spacing 0.12, text/accent. The design
		// centres the label on the badge, but Sofia Sans leaves more room under its baseline than
		// above it, so the glyphs sit high in a box that is centred by the numbers - the nudge puts
		// them back on the badge's middle, next to the crown.
		activeText: {
			marginTop: 2 * scale,
			color: achievementsColors.accent,
			fontFamily: ACHIEVEMENTS_FONT,
			fontSize: 12 * scale,
			fontWeight: '600',
			lineHeight: 12 * 1.4 * scale,
			letterSpacing: 0.12 * scale,
			includeFontPadding: false,
		},
		achievementsHeading: {
			flexDirection: 'row',
			alignItems: 'center',
			columnGap: HEADER.gap * scale,
			marginBottom: BODY.gap * scale,
		},
		sectionHeading: {
			flex: 1,
			color: achievementsColors.gold50,
			fontFamily: ACHIEVEMENTS_FONT,
			fontSize: 16 * scale,
			fontWeight: '700',
			lineHeight: 16 * scale,
			letterSpacing: -0.2813 * scale,
		},
		viewAll: {
			flexDirection: 'row',
			alignItems: 'center',
			columnGap: 4 * scale,
		},
		viewAllLabel: {
			color: achievementsColors.gold50,
			fontFamily: ACHIEVEMENTS_FONT,
			fontSize: 14 * scale,
			fontWeight: '500',
			lineHeight: 14 * 1.4 * scale,
		},
		achievements: {
			flexDirection: 'row',
			justifyContent: 'space-between',
		},
		greenPill: {
			width: BUTTON.width * scale,
			height: BUTTON.height * scale,
			alignItems: 'center',
			justifyContent: 'center',
			borderRadius: RADIUS_MD * scale,
			borderWidth: Math.max(1, 1 * scale),
			borderColor: achievementsColors.greenStroke,
			overflow: 'hidden',
			shadowColor: '#40FF33',
			shadowOffset: { width: 0, height: 0 },
			shadowOpacity: 0.7,
			shadowRadius: 5,
			elevation: 4,
		},
		greenPillLabel: {
			color: achievementsColors.gold50,
			fontFamily: ACHIEVEMENTS_FONT,
			fontSize: 14 * scale,
			fontWeight: '700',
			lineHeight: 14 * scale,
			letterSpacing: 0.14 * scale,
		},
		ownActions: {
			flexDirection: 'row',
			columnGap: 12 * scale,
		},
		profileActions: {
			flexDirection: 'row',
			alignItems: 'center',
			columnGap: BODY.gap * scale,
			height: 44 * scale,
		},
		addFriendButton: {
			flex: 1,
			height: '100%',
		},
		reportSlot: {
			flex: 1,
			height: '100%',
			zIndex: 2,
		},
		// The BLOCK CHAT / PRIVATE CHAT labels' font and size.
		addFriendLabel: {
			fontFamily: EVENT_FONT_CONDENSED,
			fontSize: 15 * scale,
			fontWeight: '800',
		},
		addFriendSurface: {
			paddingHorizontal: 8 * scale,
		},
		friendButtonSettled: {
			opacity: 0.6,
		},
		friendError: {
			marginTop: 8 * scale,
			color: '#FF8A7A',
			fontSize: 12 * scale,
			fontWeight: '600',
			textAlign: 'center',
		},
	});

export default ProfileModal;
